import { FFmpeg } from '@ffmpeg/ffmpeg';

let _ffmpeg;

function resolveBase() {
    // 1) Optional explicit override you can set in index.html: window.FFMPEG_BASE_URL = '/subapp'
    if (typeof window !== 'undefined' && window.FFMPEG_BASE_URL) {
        return String(window.FFMPEG_BASE_URL).replace(/\/+$/, '');
    }
    // 2) CRA / Webpack (set by "homepage" in package.json or env)
    if (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) {
        return String(process.env.PUBLIC_URL).replace(/\/+$/, '');
    }
    // 3) Next.js (optional)
    if (typeof process !== 'undefined' && process.env && process.env.NEXT_PUBLIC_BASE_PATH) {
        return String(process.env.NEXT_PUBLIC_BASE_PATH).replace(/\/+$/, '');
    }
    // 4) Default to root
    return '';
}

/** Ensure we’re in the browser */
const assertBrowser = () => {
    if (typeof window === 'undefined') throw new Error('FFmpeg.wasm must run in the browser.');
};

/** Lazy-load and init the FFmpeg class API */
export async function getFFmpeg() {
    assertBrowser();
    if (_ffmpeg) return _ffmpeg;
    const base = `${resolveBase()}/ffmpeg`;

    _ffmpeg = new FFmpeg();
    await _ffmpeg.load({
        coreURL:   `${base}/ffmpeg-core.js`,
        wasmURL:   `${base}/ffmpeg-core.wasm`,
        workerURL: `${base}/worker.js`      // worker will import ffmpeg.js and the *.ffmpeg.js chunks from the same folder
    });

    return _ffmpeg;
}

/** Tiny helper to get Uint8Array from a File/Blob */
const toU8 = async (file) => new Uint8Array(await file.arrayBuffer());

/** Very small “probe”: parse ffmpeg -i log for codec/res/fps/audio. */
async function probeBasic(ff, inName) {
    const info = { vCodec:null, pixFmt:null, width:null, height:null, fps:null, aCodec:null, aRate:null, aCh:null, durSec:null };
    const reVideo  = /Video:\s*([a-z0-9_]+).*?,\s*([^,]+)?,\s*(\d{2,5})x(\d{2,5}).*?(?:,?\s*(\d+(?:\.\d+)?)\s?fps)?/i;
    const reAudio  = /Audio:\s*([a-z0-9_]+).*?,\s*(\d{4,6})\s?Hz.*?,\s*(stereo|mono|2\.1|5\.1)?/i;
    const rePix    = /,\s*(yuv[0-9a-z]+)\s*,/i;
    const reDur    = /Duration:\s*(\d{2}):(\d{2}):(\d{2})\.(\d{2})/;

    const lines = [];
    const h = ({ message }) => { if (message) lines.push(message); };
    ff.on('log', h);
    try { await ff.exec(['-hide_banner','-i', inName, '-f','null','-']); } catch {}
    ff.off('log', h);

    const blob = lines.join('\n');

    const v = reVideo.exec(blob);
    if (v) {
        info.vCodec = v[1]?.toLowerCase() || null;
        info.width  = Number(v[3]) || null;
        info.height = Number(v[4]) || null;
        info.fps    = v[5] ? Number(v[5]) : null;
        const p = rePix.exec(blob);
        info.pixFmt = p ? p[1].toLowerCase() : null;
    }
    const a = reAudio.exec(blob);
    if (a) {
        info.aCodec = a[1]?.toLowerCase() || null;
        info.aRate  = Number(a[2]) || null;
        info.aCh    = a[3]?.toLowerCase() || null;
    }
    const d = reDur.exec(blob);
    if (d) {
        const [hh,mm,ss,cs] = d.slice(1).map(Number);
        info.durSec = hh*3600 + mm*60 + ss + cs/100;
    }
    return info;
}

/**
 * Ensure Instagram-compliant MP4.
 * - Remux fast if already compliant.
 * - Else encode to H.264/AAC with closed GOP, ≤1920w, 23–60fps, VBR ≤5Mbps.
 */
export async function ensureInstagramCompliant(file, { forceH264=false, enforce916=false } = {}) {
    const ff = await getFFmpeg();
    const inName = /\.mov$/i.test(file?.name || '') || file?.type === 'video/quicktime' ? 'in.mov' : 'in.mp4';
    const outNameCopy = 'out_copy.mp4';
    const outNameEnc  = 'out_enc.mp4';

    await ff.writeFile(inName, await toU8(file));

    // Probe basics
    const meta = await probeBasic(ff, inName);
    const {
        vCodec, pixFmt, width, fps, aCodec, aRate, aCh, durSec
    } = meta;

    // Basic constraints we must respect
    const durationOK = !durSec || (durSec >= 3 && durSec <= 15*60);
    const codecOK    = vCodec && (vCodec === 'h264' || (!forceH264 && vCodec === 'hevc'));
    const pixOK      = !pixFmt || pixFmt.startsWith('yuv420');   // 4:2:0 family
    const sizeOK     = !width || width <= 1920;
    const fpsOK      = !fps || (fps >= 23 && fps <= 60);
    const audioOK    = !aCodec || aCodec === 'aac';
    const rateOK     = !aRate  || aRate <= 48000;
    const chOK       = !aCh    || /stereo|mono/.test(aCh);

    // Try fast remux if everything *appears* inside bounds
    if (durationOK && codecOK && pixOK && sizeOK && fpsOK && audioOK && rateOK && chOK) {
        try {
            await ff.exec([
                '-hide_banner',
                '-i', inName,
                '-map','0:v:0?','-map','0:a:0?',
                '-c:v','copy','-c:a','copy',
                '-movflags','+faststart',
                outNameCopy
            ]);
            const data = await ff.readFile(outNameCopy);
            const blob = new Blob([data.buffer], { type: 'video/mp4' });
            const newName = (file.name || 'video').replace(/\.(mov|qt)$/i, '.mp4');
            try { await ff.deleteFile(inName); } catch {}
            try { await ff.deleteFile(outNameCopy); } catch {}
            return { file: new File([blob], newName, { type:'video/mp4', lastModified: Date.now() }), mode: 'remux' };
        } catch {/* fall through to encode */}
    }

    // Build video filter: cap width to 1920, keep AR, even dims, enforce 4:2:0
    const vf = [];
    vf.push("scale='min(iw,1920)':-2");
    vf.push('format=yuv420p');
    if (enforce916) {
        // Fit inside 1080x1920 then pad to exactly 1080x1920 (no crop)
        vf.push("scale=trunc(min(1080/iw\\,1920/ih)*iw/2)*2:trunc(min(1080/iw\\,1920/ih)*ih/2)*2");
        vf.push("pad=1080:1920:(ow-iw)/2:(oh-ih)/2:black");
    }

    // If fps outside 23–60, normalize to 30
    const fpsArgs = fps && fps >= 23 && fps <= 60 ? [] : ['-r','30'];

    // Encode to H.264/AAC, closed GOP (~2s), VBR ≤5Mbps, audio 128k@48k
    // NOTE: We cap encoder bitrate; overall file-size ≤100MB is also influenced by duration.
    const encArgs = [
        '-hide_banner',
        '-i', inName,
        '-map','0:v:0?','-map','0:a:0?',
        '-vf', vf.join(','),
        ...fpsArgs,
        '-c:v','libx264','-preset','veryfast','-profile:v','high','-level','4.1',
        '-g','60','-keyint_min','60','-sc_threshold','0','-x264-params','open_gop=0',
        '-b:v','5000k','-maxrate','5000k','-bufsize','10000k',
        '-c:a','aac','-b:a','128k','-ar','48000','-ac','2',
        '-movflags','+faststart',
        outNameEnc
    ];

    await ff.exec(encArgs);

    const out = await ff.readFile(outNameEnc);
    const blob = new Blob([out.buffer], { type: 'video/mp4' });
    const newName = (file.name || 'video').replace(/\.(mov|qt)$/i, '.mp4');

    try { await ff.deleteFile(inName); } catch {}
    try { await ff.deleteFile(outNameCopy); } catch {}
    try { await ff.deleteFile(outNameEnc); } catch {}
    console.log(`Encoding for instagram result: ${JSON.stringify(file)}`);
    return { file: new File([blob], newName, { type:'video/mp4', lastModified: Date.now() }), mode: 'encode' };
}

/** Probe video codec by parsing ffmpeg logs */
async function detectVideoCodec(ffmpeg, inName) {
    let codec = null;
    const handler = ({ message }) => {
        const m = /Video:\s*([a-z0-9_]+)/i.exec(message || '');
        if (m) codec = m[1].toLowerCase();
    };
    ffmpeg.on('log', handler);
    try { await ffmpeg.exec(['-hide_banner', '-i', inName, '-f', 'null', '-']); } catch {}
    ffmpeg.off('log', handler);
    return codec; // e.g. 'h264', 'hevc'
}

/** Remux MOV/MP4 to MP4 with +faststart if already H.264/AAC */
export async function remuxMovMp4IfPossible(file) {
    assertBrowser();
    const isMov = file?.type === 'video/quicktime' || /\.mov$/i.test(file?.name || '');
    const isMp4 = file?.type === 'video/mp4' || /\.mp4$/i.test(file?.name || '');
    if (!isMov && !isMp4) return { file, changed: false, reason: 'not-mov-or-mp4' };

    const ffmpeg = await getFFmpeg();
    ffmpeg.on('log', ({ message }) => console.debug('[ffmpeg]', message));
    await ffmpeg.exec(['-hide_banner', '-version']);
    const inName = isMov ? 'input.mov' : 'input.mp4';
    const outName = 'out.mp4';

    await ffmpeg.writeFile(inName, await toU8(file));

    // Skip client work if not H.264 (e.g., HEVC → let server transcode)
    // const vCodec = await detectVideoCodec(ffmpeg, inName);
    // if (vCodec && vCodec !== 'h264') {
    //     try { await ffmpeg.deleteFile(inName); } catch {}
    //     alert(`unsupported-codec-${vCodec}`);
    //     return { file, changed: false, reason: `unsupported-codec-${vCodec}` };
    // }

    try {
        // Fast remux: copy tracks, move moov to front
        await ffmpeg.exec([
            '-i', inName,
            '-map', '0:v:0?', '-map', '0:a:0?',
            '-c:v', 'copy', '-c:a', 'copy',
            '-movflags', '+faststart',
            outName
        ]);

        const data = await ffmpeg.readFile(outName); // Uint8Array
        const blob = new Blob([data.buffer], { type: 'video/mp4' });
        const newName = (file.name || 'video').replace(/\.(mov|qt)$/i, '.mp4');
        const remuxed = new File([blob], newName, { type: 'video/mp4', lastModified: Date.now() });

        try { await ffmpeg.deleteFile(inName); } catch {}
        try { await ffmpeg.deleteFile(outName); } catch {}

        return { file: remuxed, changed: true, reason: 'remuxed' };
    } catch (e) {
        try { await ffmpeg.deleteFile(inName); } catch {}
        try { await ffmpeg.deleteFile(outName); } catch {}
        return { file, changed: false, reason: 'remux-failed', error: e };
    }
}